import { useCallback, useMemo, useState } from "react";
import { useGetLeadTasks } from "@/modules/lead-page/hooks/get-lead-tasks.hook";
import { useTaskCompletion } from "@/modules/tasks/hooks/use-task-completion.hook";
import { TaskStatusesBackend } from "@/modules/tasks/enums";

import { TasksResponse } from "@/modules/tasks/dtos";

const useLeadTasks = () => {
  const { leadTasks, isLoading, refetch } = useGetLeadTasks();

  const [selectedTask, setSelectedTask] = useState<TasksResponse | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    handleMarkComplete,
    completingTaskId,
    isTaskOptimisticallyCompleted,
  } = useTaskCompletion({
    queryKeys: [
      "lead-tasks",
      "tasks-dashboard",
      "task-statistics",
      "tasks-count",
    ],
    successMessage: "Task completed successfully",
    onSuccess: refetch,
    refetch,
    enableOptimisticUpdates: true,
  });

  const tasks = useMemo(
    () =>
      leadTasks?.map((task) => ({
        ...task,
        status: isTaskOptimisticallyCompleted(task.uid)
          ? TaskStatusesBackend.completed
          : task.status,
      })) ?? [],
    [leadTasks, isTaskOptimisticallyCompleted],
  );

  const openTask = useCallback((task: TasksResponse) => {
    setSelectedTask(task);
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSelectedTask(null);
    setIsSidebarOpen(false);
  }, []);

  return {
    isLoading,
    tasks,
    completingTaskId,
    selectedTask,
    isSidebarOpen,
    openTask,
    closeSidebar,
    handleMarkComplete,
  };
};

export default useLeadTasks;
